import { ScanErgebnis } from '../components/gruppen/classes/scan-ergebnis';

export const OHRMARKE_SCAN_LENGTH = 10;
export const OHRMARKE_WITH_CHECK_DIGITS_LENGTH = 12;
export const PASS_SCAN_LENGTH = 15;

/**
 * Prepare scanner input for ear-tag (10) or pass (15) handling.
 * Ear-tag barcodes often transmit 10 data digits plus 2 trailing check digits.
 */
export function normalizeOhrmarkePassScanInput(raw: string): string {
    const trimmed = String(raw ?? '').trim();
    if (!trimmed) {
        return '';
    }

    const countryMatch = /^([A-Za-z]{2})(\d{10})$/.exec(trimmed);
    if (countryMatch) {
        return countryMatch[2];
    }

    const digitsOnly = trimmed.replace(/\D/g, '');
    if (digitsOnly.length === PASS_SCAN_LENGTH) {
        return digitsOnly;
    }

    if (digitsOnly.length === OHRMARKE_WITH_CHECK_DIGITS_LENGTH && digitsOnly.startsWith('27600')) {
        return digitsOnly;
    }

    if (digitsOnly.length === OHRMARKE_WITH_CHECK_DIGITS_LENGTH) {
        return digitsOnly.slice(0, OHRMARKE_SCAN_LENGTH);
    }

    return digitsOnly;
}

export function isValidOhrmarkePassScanLength(value: string): boolean {
    const len = normalizeOhrmarkePassScanInput(value).length;
    return len === OHRMARKE_SCAN_LENGTH || len === PASS_SCAN_LENGTH;
}

export function isInvalidOhrmarkePassScanLength(value: string): boolean {
    const len = normalizeOhrmarkePassScanInput(value).length;
    return len > 0 && !isValidOhrmarkePassScanLength(value);
}

/** User-facing error when scan length is not 10 (Ohrmarke) or 15 (Pass). */
export function getInvalidOhrmarkePassScanLengthMessage(value: string): string {
    const len = normalizeOhrmarkePassScanInput(value).length;
    if (len > OHRMARKE_SCAN_LENGTH && len < PASS_SCAN_LENGTH) {
        return `Fehler! Unvollständiger Pass (${len} von ${PASS_SCAN_LENGTH} Zeichen). Bitte erneut scannen.`;
    }
    return 'Fehler! Bitte eine Ohrmarke mit 10 Zeichen oder einen Pass mit 15 Zeichen eingeben (ggf. langsamer einscannen).';
}

/** Same scan type scanned again (pass after pass or ohrmarke after ohrmarke). */
export function isDuplicateScanType(
    result: { ohrmarke_gescannt?: number; pass_gescannt?: number },
    scannedInput: string,
): boolean {
    const normalized = normalizeOhrmarkePassScanInput(scannedInput);
    if (normalized.length === PASS_SCAN_LENGTH) {
        return Number(result.pass_gescannt) > 1;
    }
    if (normalized.length === OHRMARKE_SCAN_LENGTH) {
        return Number(result.ohrmarke_gescannt) > 1;
    }
    return false;
}

/** Pass and ohrmarke complement each other on an existing group animal. */
export function isComplementScanSuccess(
    result: { ohrmarke_gescannt?: number; pass_gescannt?: number },
    scannedInput: string,
): boolean {
    const normalized = normalizeOhrmarkePassScanInput(scannedInput);
    if (normalized.length === OHRMARKE_SCAN_LENGTH) {
        return Number(result.ohrmarke_gescannt) === 1 && Number(result.pass_gescannt) >= 1;
    }
    if (normalized.length === PASS_SCAN_LENGTH) {
        return Number(result.pass_gescannt) === 1 && Number(result.ohrmarke_gescannt) >= 1;
    }
    return false;
}

export function hasPassScanned(scan: { pass_gescannt?: number } | null | undefined): boolean {
    return Number(scan?.pass_gescannt) > 0;
}

export function hasOhrmarkeScanned(scan: { ohrmarke_gescannt?: number } | null | undefined): boolean {
    return Number(scan?.ohrmarke_gescannt) > 0;
}

/** Prefer 10-digit ear tag for display when both pass and ohrmarke were scanned. */
export function preferScanDisplayId(existing: string, incoming: string): string {
    const existingNormalized = normalizeOhrmarkePassScanInput(existing);
    const incomingNormalized = normalizeOhrmarkePassScanInput(incoming);
    if (incomingNormalized.length === OHRMARKE_SCAN_LENGTH) {
        return String(incoming || '').trim() || String(existing || '').trim();
    }
    if (existingNormalized.length === OHRMARKE_SCAN_LENGTH) {
        return String(existing || '').trim() || String(incoming || '').trim();
    }
    return String(incoming || '').trim() || String(existing || '').trim();
}

export function mergeScanErgebnisse(existing: ScanErgebnis, incoming: ScanErgebnis): ScanErgebnis {
    const merged = Object.assign(new ScanErgebnis(), existing, incoming);
    merged.pass_gescannt = Math.max(Number(existing.pass_gescannt || 0), Number(incoming.pass_gescannt || 0));
    merged.ohrmarke_gescannt = Math.max(
        Number(existing.ohrmarke_gescannt || 0),
        Number(incoming.ohrmarke_gescannt || 0),
    );
    merged.ohrmarke_notnull = preferScanDisplayId(
        String(existing.ohrmarke_notnull || ''),
        String(incoming.ohrmarke_notnull || ''),
    );
    if (!merged.einstallgewicht && existing.einstallgewicht) {
        merged.einstallgewicht = existing.einstallgewicht;
    }
    if (!merged.abteil && existing.abteil) {
        merged.abteil = existing.abteil;
    }
    return merged;
}
